'use client'

import { isBefore } from 'date-fns'
import { ComponentProps, useMemo } from 'react'

import { cn, createDatesBoundaryChecker, today } from '@/shared/lib'
import { Calendar } from '@/shared/ui'

type Props = ComponentProps<typeof Calendar> & {
  disableBlockedDates?: boolean // 계약 불가능한 날짜 비활성화 여부
  blocked?: Date[] // 계약 불가능한 날짜
  contracted?: Date[] // 계약된 날짜
}

export const ContractCalendar = ({
  disableBlockedDates = true,
  modifiers: modifiersProp,
  modifiersClassNames: modifiersClassNamesProp,
  classNames: classNamesProp,
  disabled,
  blocked = [],
  contracted = [],
  ...restProps
}: Props) => {
  const disabledDates = useMemo(() => {
    if (!disableBlockedDates) {
      return disabled
    }

    if (disabled === undefined) {
      return blocked
    }

    if (Array.isArray(disabled)) {
      return [...disabled, ...blocked]
    }

    return [disabled, ...blocked]
  }, [blocked, disabled, disableBlockedDates])

  const modifiers = useMemo(() => {
    const { isRangeStart, isRangeEnd } = createDatesBoundaryChecker(contracted)

    const contractedScheduled = contracted.filter(
      date => !isBefore(date, today),
    )

    return {
      blocked,
      contracted,
      contractedStart: isRangeStart,
      contractedEnd: isRangeEnd,
      contractedScheduled,
      ...modifiersProp,
    }
  }, [blocked, contracted, modifiersProp])

  const modifiersClassNames = useMemo(
    () => ({
      blocked: 'blocked',
      contracted: 'contracted',
      contractedStart: 'contracted-start',
      contractedEnd: 'contracted-end',
      contractedScheduled: 'contracted-scheduled',
      ...modifiersClassNamesProp,
    }),
    [modifiersClassNamesProp],
  )

  const classNames = useMemo(
    () => ({
      ...classNamesProp,
      day: cn(
        '[&.blocked]:before:pointer-events-none [&.blocked]:before:absolute [&.blocked]:before:top-1/2 [&.blocked]:before:left-1/2 [&.blocked]:before:h-[1px] [&.blocked]:before:w-[26px] [&.blocked]:before:-translate-x-1/2 [&.blocked]:before:-translate-y-1/2 [&.blocked]:before:bg-purple-500 [&.blocked]:before:content-[""]',
        '[&.contracted]:bg-[#EBEBEB]',
        '[&.contracted-end]:rounded-r-lg [&.contracted-start]:rounded-l-lg [&.contracted-start.contracted-end]:rounded-full',
        '[&.contracted-scheduled]:text-[#6C6C6C]',
      ),
    }),
    [classNamesProp],
  )

  return (
    <Calendar
      disabled={disabledDates}
      modifiers={modifiers}
      modifiersClassNames={modifiersClassNames}
      classNames={classNames}
      {...restProps}
    />
  )
}
